import { useMemo } from "react";

import {
  deriveEarlyRepaymentPlan,
  type IEarlyRepaymentPlan,
} from "./earlyRepayment";
import type { IRepaymentPlanData } from "./types";

export type MoneyColumn =
  | "payment"
  | "interest"
  | "principal"
  | "remainingBalance";

// No fees column: the plan carries no recurring fees, and the one-off setup
// fee is month 0's payment.
export const MONEY_COLUMNS: MoneyColumn[] = [
  "payment",
  "interest",
  "principal",
  "remainingBalance",
];

export interface IRepaymentPlanDisplay {
  displayPlan: IEarlyRepaymentPlan;
  totalValue: (key: MoneyColumn) => number | null;
}

export const useRepaymentPlanDisplay = (
  plan: IRepaymentPlanData,
  months: number,
): IRepaymentPlanDisplay => {
  return useMemo(() => {
    const fullMonths = plan.entries.length - 1;
    const clampedMonths = Math.min(Math.max(months, 1), fullMonths);
    const displayPlan = deriveEarlyRepaymentPlan(plan, clampedMonths);

    const totalValue = (key: MoneyColumn): number | null => {
      switch (key) {
        case "payment":
          return Number(displayPlan.totals.totalPayment);
        case "interest":
          return Number(displayPlan.totals.totalInterest);
        case "principal":
          return Number(displayPlan.totals.totalPrincipal);
        default:
          return null;
      }
    };

    return {
      displayPlan,
      totalValue,
    };
  }, [plan, months]);
};
